import { useEffect, useLayoutEffect, useRef, useMemo, useState } from "react"
import { pick, omit, omitBy, isNil, isEqual, debounce, pickBy } from "lodash"
import { useSaveContextPreferencesDual, useReadContextPreference } from "hooks/preferences"
import { OK, ERROR } from "helpers/data-statuses"

/**
 * Single hook that handles both loading and saving preferences
 * Replaces the need for separate PreferenceSaver and PreferenceLoader components
 *
 * @param {string} subType - The preference subtype (e.g., "dashboard", "asset-view")
 * @param {ReadonlyArray<string>} preferenceFields - Array of field names to save as preferences
 * @param {object} state - Current state to save preferences from
 * @param {function} dispatch - Dispatch function to update state with loaded preferences
 * @param {function} setPreferencesAction - Action creator for setting preferences (e.g., setPreferences)
 * @param {object} [options] - Optional configuration
 * @param {object} [options.setPreferenceValidationOptions] - Function that receives preferences to validate
 *  and a callback to set the validation result. The callback will receive the validation result with the preferences
 *  and isCorrected boolean indicating if the preferences were corrected.
 * @param {number} [options.debounceTime=300] - Debounce time for saving (default 300ms)
 * @param {string} [options.fallbackSubType] - When set, if the primary key has no saved preferences, load from this
 *  key instead (e.g. legacy static key for panel preference migration).
 * @param {string[]} [options.omitLoadedKeys] - Preference keys to drop before dispatching loaded data (applies to
 *  primary and fallback). Prefer `omitFallbackKeys` when only legacy/fallback merge should ignore a key.
 * @param {string[]} [options.omitFallbackKeys] - When primary has no data and fallback is used, drop these keys from
 *  the merged payload (e.g. omit `view` so legacy migration does not override the panel default view).
 * @param {string} [options.mirrorSaveSubType] - When set and different from `subType`, saves also write to this key
 *  (e.g. per-panel key while primary reads/writes legacy).
 * @param {boolean} [options.skipInitialLoad=false] - Skip the one-shot load dispatch while still reporting loaded and
 *  saving on change. Set when the caller's store slot is already authoritative at mount (e.g. a `key`-remount that
 *  returns to a dashboard whose RTK slice still holds live, possibly-unsaved state) so the cached read does not
 *  clobber it.
 * @returns {boolean} isPreferencesLoaded - Whether preferences have been loaded
 */
export function usePreferences(
  subType,
  preferenceFields,
  state,
  dispatch,
  setPreferencesAction,
  {
    setPreferenceValidationOptions,
    debounceTime = 300,
    fallbackSubType,
    omitLoadedKeys,
    omitFallbackKeys,
    mirrorSaveSubType,
    disabled = false,
    skipInitialLoad = false,
  } = {},
) {
  const savePreferences = useSaveContextPreferencesDual(subType, mirrorSaveSubType)
  const [preferences, preferenceStatus] = useReadContextPreference(subType)
  const fallbackEnabled = !!fallbackSubType && fallbackSubType !== subType
  const [fallbackPreferences, fallbackStatus] = useReadContextPreference(
    fallbackSubType ?? subType,
    undefined,
    { enabled: fallbackEnabled },
  )
  const [validationResult, setValidationResult] = useState(null)

  const hasPrimaryFinished = preferenceStatus === OK || preferenceStatus === ERROR
  const hasFallbackFinished = !fallbackEnabled || fallbackStatus === OK || fallbackStatus === ERROR
  const hasPreferenceRequestFinished = hasPrimaryFinished && hasFallbackFinished

  // Derived: loaded when disabled (no prefs needed); when skipInitialLoad (the slot is already
  // authoritative and we deliberately neither load nor validate — so report loaded immediately,
  // otherwise the validation-gated branch below never flips and the save effect stays disabled,
  // silently stopping edits from persisting after a remount); when the async request finished and
  // no validation is required; or when validation has produced a result to apply.
  const isPreferencesLoaded = disabled
    || skipInitialLoad
    || (!setPreferenceValidationOptions && hasPreferenceRequestFinished)
    || (!!setPreferenceValidationOptions && validationResult !== null)

  // ===== LOADING LOGIC (runs once on mount) =====
  // Pre-latch when skipInitialLoad so the load effect early-returns: the slot is already
  // authoritative, so dispatching the cached read would overwrite live (possibly unsaved) state.
  const isInitialLoadRef = useRef(skipInitialLoad)
  const lastValidationResultRef = useRef(null)

  // ===== SAVING LOGIC (runs on state changes) =====
  const previousPreferencesRef = useRef(null)
  const isInitialSaveRef = useRef(true)

  const prevSubTypeRef = useRef(subType)
  useLayoutEffect(() => {
    if (prevSubTypeRef.current === subType) {
      return
    }
    prevSubTypeRef.current = subType
    isInitialLoadRef.current = false
    isInitialSaveRef.current = true
    lastValidationResultRef.current = null
  }, [subType])

  // Create a debounced save function to avoid rapid successive saves
  const debouncedSave = useMemo(
    () => debounce((preferencesToSave) => {
      savePreferences(preferencesToSave)
    }, debounceTime),
    [savePreferences, debounceTime],
  )

  // useLayoutEffect ensures the preference dispatch fires in the same commit phase as the
  // render where isPreferencesLoaded becomes true, preventing a one-frame window where
  // consumers see isPreferencesLoaded=true but Redux state still has defaults.
  useLayoutEffect(() => {
    if (isInitialLoadRef.current) {
      return
    }

    if (disabled) {
      return
    }

    if (hasPreferenceRequestFinished) {
      isInitialLoadRef.current = true

      const cleanedPrimary = preferences ? pickBy(preferences, (value) => !isNil(value)) : {}
      const cleanedFallback = fallbackPreferences ? pickBy(fallbackPreferences, (value) => !isNil(value)) : {}
      const hasPrimaryData = Object.keys(cleanedPrimary).length > 0
      const hasFallbackData = Object.keys(cleanedFallback).length > 0
      const cleanedPreferences = hasPrimaryData ? cleanedPrimary : (hasFallbackData ? cleanedFallback : cleanedPrimary)
      let preferencesToApply =
        omitLoadedKeys && omitLoadedKeys.length > 0
          ? omit(cleanedPreferences, omitLoadedKeys)
          : cleanedPreferences
      if (
        !hasPrimaryData
        && hasFallbackData
        && omitFallbackKeys
        && omitFallbackKeys.length > 0
      ) {
        preferencesToApply = omit(preferencesToApply, omitFallbackKeys)
      }

      if (setPreferenceValidationOptions) {
        setPreferenceValidationOptions({ preferences: preferencesToApply, callback: setValidationResult })
      } else {
        if (Object.keys(preferencesToApply).length > 0) {
          dispatch(setPreferencesAction(preferencesToApply))
        }
      }
    }
  }, [
    subType,
    disabled,
    hasPreferenceRequestFinished,
    preferences,
    fallbackPreferences,
    setPreferenceValidationOptions,
    setValidationResult,
    setPreferencesAction,
    dispatch,
    omitLoadedKeys,
    omitFallbackKeys,
  ])

  useLayoutEffect(() => {
    if (disabled) {
      return
    }
    if (validationResult !== null && validationResult !== lastValidationResultRef.current) {
      lastValidationResultRef.current = validationResult
      if (validationResult.isCorrected) {
        debouncedSave(validationResult.preferences)
      }
      dispatch(setPreferencesAction(validationResult.preferences))
    }
  }, [disabled, validationResult, debouncedSave, setPreferencesAction, dispatch])

  useEffect(() => {
    if (!isPreferencesLoaded || !state || disabled) {
      return
    }

    const currentPreferences = omitBy(pick(state, preferenceFields), isNil)

    if (isInitialSaveRef.current) {
      previousPreferencesRef.current = currentPreferences
      isInitialSaveRef.current = false
      return
    }

    if (!isEqual(previousPreferencesRef.current, currentPreferences)) {
      previousPreferencesRef.current = currentPreferences
      if (Object.keys(currentPreferences).length > 0) {
        debouncedSave(currentPreferences)
      }
    }
  }, [state, isPreferencesLoaded, debouncedSave, preferenceFields, disabled])

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSave.cancel()
    }
  }, [debouncedSave])

  return isPreferencesLoaded
}
